import { useAtsConversationApi, useAtsConversationApiWithOptions } from '@/composables/useApi';
import SearchUtils from '@/utils/search-utils';
import type {
  ConversationIndex,
  ConversationIndexHeaders,
  FetchConversationIndexByApplicantIdInput,
  FetchConversationIndexesInput,
} from './conversation-index.type';

export class ConversationIndexPersistence {
  public async fetchConversationIndexes(
    params: FetchConversationIndexesInput,
  ): Promise<ConversationIndex[]> {
    const searchUrl = SearchUtils.convertSearchObjectToSearchUrl(params.params);
    const url = `conversation/employer/${params.employerId}/headers?${searchUrl}`;
    // Each list fetch is its own generation, so an identical in-flight GET must not answer for it.
    const { data, error } = await useAtsConversationApiWithOptions({ dedupe: 'off' })(url)
      .get()
      .json<ConversationIndexHeaders>();

    if (error.value) {
      throw new Error(error.value);
    }

    return data.value?.headers || [];
  }

  public async fetchConversationIndexByApplicantId(
    params: FetchConversationIndexByApplicantIdInput,
  ): Promise<ConversationIndex> {
    const url = `conversation/employer/${params.employerId}/application/${params.applicationId}`;
    const { data, error } = await useAtsConversationApi(url).get().json<ConversationIndex>();

    if (error.value || !data.value) {
      throw new Error(error.value);
    }

    return data.value;
  }

  public async fetchConversationIndexesByProData(params: {
    employerId: number;
    proData: string;
    /**
     * Opt-in identity matching mode (ONE-6912): phone-like queries match the
     * digit-normalized phone only; text queries match name and the email
     * local part unless the query contains `@`. Omitted for Candidate
     * Archive, which keeps today's matching. Older backends ignore the param.
     */
    strictIdentityMatch?: boolean;
  }): Promise<{
    count: number;
    headers: ConversationIndex[];
    /**
     * Strict mode only: true when the backend matched more candidates than
     * the 100 it returned (`count` is then the returned-row count, not a
     * total). Absent on the legacy path and from older backends — callers
     * must treat a missing value as false.
     */
    hasMore?: boolean;
  }> {
    const query = new URLSearchParams({
      proData: params.proData,
      ...(params.strictIdentityMatch ? { strictIdentityMatch: 'true' } : {}),
    }).toString();

    const url = `/conversation/employer/${params.employerId}/conversation-headers?${query}`;
    const { data, error } = await useAtsConversationApi(url).get().json<{
      count: number;
      headers: ConversationIndex[];
      hasMore?: boolean;
    }>();

    if (error.value) {
      throw new Error(error.value);
    }

    if (!data.value) {
      throw new Error('No conversation indexes found');
    }

    return data.value;
  }

  public async updateConversationWatchStatus(
    conversationId: number,
    isWatching: boolean,
  ): Promise<{ isWatching: boolean }> {
    const url = `conversation/${conversationId}/watch-status`;
    const { data, error } = await useAtsConversationApi(url)
      .patch({ isWatching })
      .json<{ isWatching: boolean }>();

    if (error.value || !data.value) {
      throw new Error(error.value || 'Failed to update conversation alerts');
    }

    return data.value;
  }
}
